/**
 * Utility functions for phone number formatting and validation
 */

/**
 * Formats a phone number for display in the format (XXX) XXX-XXXX
 * @param phoneNumber - Raw phone number string
 * @returns Formatted phone number string
 */
export function formatPhoneNumberForDisplay(phoneNumber: string | undefined | null): string {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Handle different lengths
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  
  // Handle 11-digit numbers (with country code)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  
  // For longer numbers, just show the last 10 digits formatted
  const last10 = digits.slice(-10);
  return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
}

/**
 * Formats a phone number for input fields in the format XXX-XXX-XXXX
 * @param value - Input value from user
 * @returns Formatted phone number for input
 */
export function formatPhoneNumberForInput(value: string): string {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
}

/**
 * Cleans a phone number for SMS/calling purposes
 * @param phoneNumber - Raw phone number string
 * @returns Clean phone number with only digits
 */
export function cleanPhoneNumber(phoneNumber: string | undefined | null): string {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/\D/g, '');
}

/**
 * Validates if a phone number is valid (US format)
 * @param phoneNumber - Phone number to validate
 * @returns true if valid, false otherwise
 */
export function isValidPhoneNumber(phoneNumber: string | undefined | null): boolean {
  if (!phoneNumber) return false;
  const digits = cleanPhoneNumber(phoneNumber);
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

/**
 * Gets a list of members with valid phone numbers
 * @param members - Array of members
 * @returns Array of members with valid phone numbers
 */
export function getMembersWithPhoneNumbers<T extends { phone?: string; mobile?: string }>(
  members: T[]
): T[] {
  return members.filter(member => {
    const phoneNumber = member.phone || member.mobile;
    return isValidPhoneNumber(phoneNumber);
  });
}

/**
 * Creates an SMS URL for multiple recipients (group text)
 * @param phoneNumbers - Array of phone numbers
 * @param message - Message to send
 * @returns SMS URL string
 */
export function createGroupSMSUrl(phoneNumbers: string[], message: string): string {
  const cleanNumbers = phoneNumbers
    .map(cleanPhoneNumber)
    .filter(number => number.length >= 10)
    .map(number => {
      // Ensure we have a proper 10 or 11 digit number
      if (number.length === 10) {
        return number;
      } else if (number.length === 11 && number.startsWith('1')) {
        return number.substring(1); // Remove country code for better compatibility
      } else if (number.length > 11) {
        // Take the last 10 digits
        return number.slice(-10);
      }
      return number;
    })
    .filter(number => number.length === 10);
  
  const encodedMessage = encodeURIComponent(message);
  
  // Use comma-separated format which is more widely supported
  // Some platforms limit the number of recipients, but this should work for most
  const recipients = cleanNumbers.join(',');
  
  // Standard SMS URL format
  const smsUrl = `sms:${recipients}?body=${encodedMessage}`;
  
  return smsUrl;
}

/**
 * Formats a phone number to E.164 format (+1XXXXXXXXXX)
 * @param phoneNumber - Raw phone number string
 * @returns E.164 formatted phone number
 */
export function formatPhoneNumberE164(phoneNumber: string | undefined | null): string {
  if (!phoneNumber) return '';
  
  const digits = cleanPhoneNumber(phoneNumber);
  if (!digits) return '';
  
  // Handle different lengths
  if (digits.length === 10) {
    return `+1${digits}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  } else if (digits.length > 11) {
    // Take the last 10 digits and add US country code
    return `+1${digits.slice(-10)}`;
  }
  
  return '';
}

/**
 * Creates an SMS URL for a single recipient
 * @param phoneNumber - Phone number
 * @param message - Message to send
 * @returns SMS URL string
 */
export function createSMSUrl(phoneNumber: string, message: string): string {
  const e164Number = formatPhoneNumberE164(phoneNumber);
  if (!e164Number) return '';
  
  const encodedMessage = encodeURIComponent(message);
  return `sms:${e164Number}?body=${encodedMessage}`;
}
